import React, { useState, useRef } from 'react';
import {
  Carousel, CarouselItem, Heading, useMediaQuery, mediaQueries,
} from '@americanexpress/dls-react';
import { useIntl } from 'react-intl';
import { useReactlytics } from '@americanexpress/reactlytics';
import { OnVisible } from '@americanexpress/data-manager-utils';
import { InspirationCard } from './InspirationCards/InspirationCard';
import { useGetInspirationalArticlesHook } from '../../hooks/useGetInspirationalArticlesHook';
import { ExploreMoreInspirationCard } from './ExploreMoreInspirationCard/ExploreMoreInspirationCard';
import { InspirationFilterButtons } from './InspirationFilterButtons/InspirationFilterButtons';
import 'react-loading-skeleton/dist/skeleton.css';
import { GET_INSPIRED_IMAGE_BASE_PATH, GET_INSPIRED_IMAGE_SIZE } from '../../constants/pathnameConstants';
import { useFeatureFlagWithProperties } from '../../thunks/useFeatureFlagWithProperties';
import { GET_INSPIRED_FF } from '../../constants/oneXpConstants';
import { useHasEnteredViewport } from '../../hooks/useHasEnteredViewportHook';
import { InspirationSkeletonCards } from './InspirationSkeletonCards/InspirationSkeletonCards';
import { MODULE_NAME } from '../../constants/modules';
import { createAnalytics } from '../../utils/createAnalyticsUtil';
import { removeSpecialChartersAndWhiteSpace } from '../../utils/removeSpecialChartersAndWhiteSpace';

export const InspirationArticles = () => {
  const { track } = useReactlytics();
  const ref = useRef(null);
  const hasLoadedIntoViewport = useHasEnteredViewport(ref, { threshold: 0.1 });
  const { formatMessage } = useIntl();
  const shouldDisplayGetInspired = useFeatureFlagWithProperties(GET_INSPIRED_FF);
  const shouldDisplayCardSlider = useMediaQuery(mediaQueries.LG_DOWN);
  const {
    data: getInspiredArticles,
    error,
    isLoading,
  } = useGetInspirationalArticlesHook(hasLoadedIntoViewport);
  const [selectedCategory, setSelectedCategory] = useState('Articles');
  const triggerOnClickAnalytics = createAnalytics(track, {
    element: `hp-get-inspired-${selectedCategory.toLowerCase()}`,
    type: 'click',
  });

  if (!shouldDisplayGetInspired || error) {
    return null;
  }

  if (isLoading || !hasLoadedIntoViewport || (!isLoading && !getInspiredArticles)) {
    return (
      <InspirationSkeletonCards
        hasLoadedIntoViewport={hasLoadedIntoViewport}
        reference={ref}
      />
    );
  }

  const shouldShowExploreMoreCard = getInspiredArticles[selectedCategory].length < 4;
  const selectedInspirationCards = getInspiredArticles[selectedCategory].map((item) => (
    <InspirationCard
      key={item.articleTitle}
      title={item.articleTitle}
      description={item.shortDescription}
      imageUrl={`${GET_INSPIRED_IMAGE_BASE_PATH}/${item.supplierId}/${item.photoId}/${GET_INSPIRED_IMAGE_SIZE}/${item.originalImgName}`}
      imageAlt={item.heroPhotoAlt}
      linkUrl={item.webViewLink}
      triggerOnClickAnalytics={triggerOnClickAnalytics}
    />
  ));

  return (
    <OnVisible
      analyticsInfo={{
        element: 'hp-get-inspired',
        detail: selectedCategory.toLowerCase(),
      }}
    >
      <section className="container pad-1-t pad-4-b">
        <div data-testid={`${MODULE_NAME}-homepage-get-inspired-container`}>
          <Heading level={2} variant="sans-medium-regular" className="text-align-left pad-3-b">
            {formatMessage({ id: 'getInspired.heading' })}
          </Heading>
          <InspirationFilterButtons
            activeTag={selectedCategory}
            setActiveTag={setSelectedCategory}
            isInViewport={hasLoadedIntoViewport}
            triggerOnClickAnalytics={triggerOnClickAnalytics}
          />
          { shouldDisplayCardSlider ? (
            <Carousel
              id={selectedCategory}
              isSlideFullWidth={false}
              isInfinite={true}
            >
              {[
                ...selectedInspirationCards.map((item) => (
                  <CarouselItem
                    id={item.props.title}
                    key={item.props.title}
                    reactlytics={{
                      detail: `hp-get-inspired-carousel-item-${removeSpecialChartersAndWhiteSpace(item.props.title)}`,
                      element: selectedCategory.toLowerCase(),
                    }}
                  >
                    {item}
                  </CarouselItem>
                )
                ),
                shouldShowExploreMoreCard ? (
                  <CarouselItem id="exploreMoreArticles" key="exploreMoreArticles">
                    <ExploreMoreInspirationCard triggerOnClickAnalytics={triggerOnClickAnalytics} />
                  </CarouselItem>
                ) : []]}
            </Carousel>
          ) : (
            <div className="row">
              {selectedInspirationCards.map((item) => (
                <div className="col-lg-3" key={item.props.title}>
                  {item}
                </div>
              ))}
              {shouldShowExploreMoreCard && (
                <div className="col-lg-3" key="exploreMoreArticles">
                  <ExploreMoreInspirationCard />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </OnVisible>
  );
};
